/**
 * Service de génération du formulaire de pouvoir (procuration)
 *
 * Utilise docxtemplater pour générer un document Word à partir d'un template
 * conforme aux exigences légales (Article 22 de la loi du 10 juillet 1965)
 */

import Docxtemplater from 'docxtemplater'
import PizZip from 'pizzip'
import { saveAs } from 'file-saver'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import type { Meeting } from '../types'

// ===== TYPES =====

export interface PouvoirOwnerData {
  id: string
  first_name: string
  last_name: string
  civility?: 'M' | 'MME' | null
  address_line1?: string | null
  address_line2?: string | null
  postal_code?: string | null
  city?: string | null
  email?: string | null
  tantiemes?: number
}

export interface PouvoirResidenceData {
  id: string
  name: string
  code?: string | null
  address?: string | null
  city?: string | null
  postal_code?: string | null
  tantiemes_total?: number
}

export interface PouvoirGenerationParams {
  meeting: Meeting
  owner: PouvoirOwnerData
  residence: PouvoirResidenceData
  lieuEmission?: string
}

export interface PouvoirFormData {
  // En-tête
  lieu_emission: string
  date_emission: string
  tantiemes: number
  tantiemes_total: number
  reference: string

  // Destinataire (mandant pré-rempli)
  destinataire_civilite_nom: string
  destinataire_adresse_ligne1: string
  destinataire_adresse_cp_ville: string

  // Mandant
  coproprietaire_nom: string

  // Copropriété
  copropriete_code: string
  copropriete_nom: string
  copropriete_adresse: string
  copropriete_cp_ville: string

  // AG
  ag_jour_semaine: string
  ag_date: string
  ag_heure: string
  ag_lieu: string
}

// ===== HELPER FUNCTIONS =====

/**
 * Génère la référence du pouvoir
 */
function generateReference(owner: PouvoirOwnerData, residence: PouvoirResidenceData): string {
  const residenceCode = residence.code || residence.id.slice(0, 4).toUpperCase()
  const ownerRef = owner.id.slice(0, 6).toUpperCase()
  return `${residenceCode}_${ownerRef}`
}

/**
 * Formate la civilité avec le nom
 */
function formatCivilityNom(civility: 'M' | 'MME' | null | undefined, lastName: string, firstName: string): string {
  const civilityLabel = civility === 'M' ? 'Monsieur' : civility === 'MME' ? 'Madame' : ''
  return `${civilityLabel} ${lastName.toUpperCase()} ${firstName}`.trim()
}

/**
 * Retourne le jour de la semaine
 */
function getDayOfWeek(date: Date): string {
  return format(date, 'EEEE', { locale: fr })
}

/**
 * Prépare les données pour le template de pouvoir
 */
export function preparePouvoirFormData(params: PouvoirGenerationParams): PouvoirFormData {
  const { meeting, owner, residence, lieuEmission } = params

  const meetingDate = new Date(meeting.meeting_date)

  // Formater l'heure (HH:MM)
  const startTime = meeting.start_time || '14:00'
  const formattedTime = startTime.length > 5 ? startTime.substring(0, 5) : startTime

  // Adresse copropriété formatée
  const coproprieteAdresse = residence.address || ''
  const coproprieteCpVille = [residence.postal_code, residence.city?.toUpperCase()].filter(Boolean).join(' ')

  return {
    // En-tête
    lieu_emission: lieuEmission || residence.city || 'Paris',
    date_emission: format(new Date(), 'd MMMM yyyy', { locale: fr }),
    tantiemes: owner.tantiemes || 0,
    tantiemes_total: residence.tantiemes_total || 10000,
    reference: generateReference(owner, residence),

    // Destinataire (mandant pré-rempli)
    destinataire_civilite_nom: formatCivilityNom(owner.civility, owner.last_name, owner.first_name),
    destinataire_adresse_ligne1: owner.address_line1?.toUpperCase() || '',
    destinataire_adresse_cp_ville: [owner.postal_code, owner.city?.toUpperCase()].filter(Boolean).join(' '),

    // Mandant
    coproprietaire_nom: `${owner.last_name.toUpperCase()} ${owner.first_name}`,

    // Copropriété
    copropriete_code: residence.code || residence.id.slice(0, 4).toUpperCase(),
    copropriete_nom: residence.name.toUpperCase(),
    copropriete_adresse: coproprieteAdresse,
    copropriete_cp_ville: coproprieteCpVille,

    // AG
    ag_jour_semaine: getDayOfWeek(meetingDate),
    ag_date: format(meetingDate, 'd MMMM yyyy', { locale: fr }),
    ag_heure: formattedTime,
    ag_lieu: meeting.location || 'À préciser',
  }
}

// ===== MAIN GENERATION FUNCTION =====

/**
 * Génère le pouvoir et retourne un Blob
 */
export async function generatePouvoir(params: PouvoirGenerationParams): Promise<Blob> {
  // Charger le template
  const templateUrl = '/templates/pouvoir-template.docx'
  const response = await fetch(templateUrl)

  if (!response.ok) {
    throw new Error(`Template non trouvé: ${templateUrl}`)
  }

  const templateArrayBuffer = await response.arrayBuffer()

  // Créer le document avec PizZip
  const zip = new PizZip(templateArrayBuffer)
  const doc = new Docxtemplater(zip, {
    paragraphLoop: true,
    linebreaks: true,
    // Options pour éviter les erreurs sur les tags non trouvés
    nullGetter: () => '',
  })

  // Préparer les données
  const data = preparePouvoirFormData(params)

  // Remplir le template
  doc.render(data)

  // Générer le blob
  const blob = doc.getZip().generate({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  })

  return blob
}

/**
 * Génère et télécharge le pouvoir
 */
export async function downloadPouvoir(params: PouvoirGenerationParams): Promise<void> {
  const blob = await generatePouvoir(params)

  // Générer le nom du fichier
  const meetingDate = new Date(params.meeting.meeting_date)
  const dateStr = format(meetingDate, 'yyyyMMdd')
  const ownerName = `${params.owner.last_name}_${params.owner.first_name}`.replace(/\s+/g, '_')
  const fileName = `POUVOIR_${dateStr}_${ownerName}.docx`

  // Télécharger
  saveAs(blob, fileName)
}
